import moment from "moment-timezone";

/**
 * Date formatting utilities
 * Provides consistent date/time formatting across the application
 */

/**
 * Format a date in "DD MMM YYYY" format (e.g., "22 Oct 2025")
 * @param {string|Date} date - Date to format
 * @returns {string} Formatted date string
 */
export const formatDate = date => {
  if (!date) return "";
  return moment(date).format("DD MMM YYYY");
};

/**
 * Format a time in "HH:mm" format (e.g., "14:30")
 * @param {string|Date} date - Date/time to format
 * @returns {string} Formatted time string
 */
export const formatTime = date => {
  if (!date) return "";
  return moment(date).format("HH:mm");
};

/**
 * Format a date with both date and time components
 * Returns an object with separate date and time strings for flexible display
 * @param {string|Date} date - Date to format
 * @returns {{ date: string, time: string }} Object with formatted date and time
 */
export const formatDateTime = date => {
  if (!date) return { date: "", time: "" };

  return {
    date: moment(date).format("DD MMM YYYY"),
    time: moment(date).format("HH:mm"),
  };
};

/**
 * Format a date with timezone awareness
 * @param {string|Date} date - Date to format
 * @param {string} timezone - IANA timezone (optional, defaults to user's timezone)
 * @returns {{ date: string, time: string }} Object with formatted date and time
 */
export const formatDateTimeWithTimezone = (date, timezone = null) => {
  if (!date) return { date: "", time: "" };

  const momentDate = timezone ? moment(date).tz(timezone) : moment(date);

  return {
    date: momentDate.format("DD MMM YYYY"),
    time: momentDate.format("HH:mm"),
  };
};

export default {
  formatDate,
  formatTime,
  formatDateTime,
  formatDateTimeWithTimezone,
};
